import { Checkbox, FormControlLabel, IconButton, Typography } from '@material-ui/core';
import { Visibility, VisibilityOff } from '@material-ui/icons';
import React, { useEffect, useState } from 'react';
import FacebookLogin from 'react-facebook-login';
import { Formik, FormikHelpers } from 'formik';
import { useHistory } from 'react-router-dom';
import AppleLogin from 'react-apple-login';
import toast from 'react-hot-toast';
import isEmpty from 'lodash';

import { userAuthService, userService } from '@project/services';

import appConfig from '../../../config';
import { Button, FormTextField } from '../../atoms';
import { loginValidation } from '../../../validators';
import {
  LoginProps,
  ErrorObject,
  BaseFormProps,
  FacebookResponse,
  GoogleResponse,
} from '../../../types';

export const LoginForm: React.FC<BaseFormProps<LoginProps>> = ({
  initialValues,
  submitForm,
  onSuccess,
  onFailure,
}) => {
  const history = useHistory();

  const _handleFormSubmitError = (error: ErrorObject, actions: FormikHelpers<LoginProps>) => {
    actions.setSubmitting(false);
    const apiErrors = error.errors;
    if (!isEmpty(apiErrors)) {
      actions.setFieldError('username', apiErrors.username);
      actions.setFieldError('password', apiErrors.password);
    } else if (error.statusCode === 400) {
      actions.setFieldError(
        'password',
        error.message === 'invalid_grant' ? 'Invalid email or password' : error.message,
      );
    } else {
      actions.setFieldError('username', error.message);
    }
  };

  const _handleSubmission = async (formData: LoginProps, actions: FormikHelpers<LoginProps>) => {
    const result = await userService.canLogin(formData.username).catch(() => null);

    if (result && !result.data.canLogin) {
      toast.error(
        'Your account has been disabled. Please contact our support team for assistance.',
      );
      actions.setSubmitting(false);
      return;
    }

    submitForm(formData)
      .then(() => {
        actions.setSubmitting(false);
        onSuccess();
      })
      .catch((error: ErrorObject) => {
        _handleFormSubmitError(error, actions);
        onFailure();
      });
  };

  const responseFacebook = async (response: FacebookResponse) => {
    if (!response.accessToken) {
      return;
    }

    const data = await userAuthService.facebookExternalSignIn({
      provider: 'facebook',
      accessToken: response.accessToken,
    });

    if (data === false) {
      history.push('/sign-up');
      return;
    }

    onSuccess();
  };

  const responseGoogle = async (response: GoogleResponse) => {
    if (!response.credential) {
      return;
    }

    const data = await userAuthService.googleExternalSignIn({
      provider: 'google',
      credential: response.credential,
    });

    if (data === false) {
      history.push('/sign-up');
      return;
    }

    onSuccess();
  };

  const responseApple = async (response) => {
    if (!response.authorization) {
      return;
    }
    const idToken = response.authorization.id_token;

    const data = await userAuthService.appleExternalSignIn({ provider: 'apple', idToken });

    if (data === false) {
      history.push('/sign-up');
      return;
    }

    onSuccess();
  };

  const _onForgotPasswordClick = () => {
    history.push('/forgot-password');
  };

  const _onSignUpClick = () => {
    history.push('/sign-up');
  };
  const [showPassword, setShowPassword] = useState(false);

  function _toggleShowPassword() {
    setShowPassword(!showPassword);
  }

  const buttonIcon = showPassword ? <Visibility /> : <VisibilityOff />;

  useEffect(() => {
    if (sessionStorage.getItem('preSignUpEmail')) {
      sessionStorage.removeItem('preSignUpEmail');
    }

    const initializeGoogleSignIn = () => {
      if (window.google) {
        window.google.accounts.id.initialize({
          client_id: appConfig.googleClientId,
          callback: responseGoogle,
          auto_select: false,
          cancel_on_tap_outside: false,
        });
      }
    };

    if (!window.google) {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initializeGoogleSignIn;
      document.head.appendChild(script);
    } else {
      initializeGoogleSignIn();
    }
  }, []);

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={loginValidation}
      validateOnBlur
      validateOnChange={false}
      onSubmit={_handleSubmission}
      enableReinitialize
    >
      {({
        handleSubmit,
        isSubmitting,
        values,
        touched,
        errors,
        handleChange,
        handleBlur,
        handleReset,
        setFieldValue,
      }) => {
        const handleGoogleSignIn = () => {
          if (window.google) {
            setFieldValue('isLoading', true);

            window.google.accounts.id.prompt((notification) => {
              setFieldValue('isLoading', false);

              if (notification.isNotDisplayed()) {
                const reason = notification.getNotDisplayedReason();

                if (
                  reason === 'browser_not_supported' ||
                  reason === 'suppressed_by_user' ||
                  reason === 'unknown_reason'
                ) {
                  toast.error(
                    'Google sign-in was skipped. Please try again using your email and password.',
                  );
                }
              } else if (notification.isSkippedMoment()) {
                toast.error(
                  'Google sign-in failed. Please enable third-party sign-in or use your email and password.',
                );
              }
            });
          }
        };

        const onBlurEmail = (fieldName): void => {
          if (values.username.indexOf(' ') >= 0) {
            setFieldValue(fieldName, values.username.trim());
          }
          handleBlur(fieldName);
        };

        return (
          <form onReset={handleReset} onSubmit={handleSubmit} className="auth-form-body">
            <p className="auth-heading">Sign In</p>
            <FormTextField
              variableName="username"
              placeholder="Email"
              autoComplete="username"
              values={values}
              onChange={handleChange}
              onBlur={onBlurEmail}
              touched={touched}
              errors={errors}
              icon={<img src="assets/icons/mail-icon.png" alt="mail" />}
              required
              className="w-4/5 self-center"
            />
            <FormTextField
              variableName="password"
              placeholder="Password"
              autoComplete="current-password"
              type={showPassword ? 'text' : 'password'}
              values={values}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={touched}
              errors={errors}
              icon={<img src="assets/icons/lock-icon.png" alt="password" />}
              endIcon={
                <IconButton size="small" onClick={_toggleShowPassword}>
                  {buttonIcon}
                </IconButton>
              }
              required
              className="w-4/5 self-center"
            />
            <div className="flex space-x-2 h-10 max-sm:px-8 max-sm:justify-center w-4/5">
              <div className="flex items-center text-sm justify-center w-full px-1 bg-blue-600 hover:bg-blue-400 text-white rounded-lg shadow-md transition duration-150 ease-in-out max-sm:w-12 max-sm:h-12 max-sm:rounded-full max-sm:p-0">
                <FacebookLogin
                  appId={appConfig.facebookAppId}
                  autoLoad={false}
                  fields="name,email"
                  icon={
                    <i
                      className="fa fa-facebook"
                      style={{ fontSize: '16px', marginRight: '8px', marginTop: '4px' }}
                    />
                  }
                  callback={responseFacebook}
                  textButton="Sign in with Facebook"
                  cssClass="my-facebook-button-class sm:block hidden"
                />
                <div className="sm:hidden flex items-center justify-center w-full h-full">
                  <FacebookLogin
                    appId={appConfig.facebookAppId}
                    autoLoad={false}
                    fields="name,email"
                    icon="fa-facebook"
                    callback={responseFacebook}
                    textButton=""
                    cssClass="facebook-icon-only"
                  />
                </div>
              </div>
              <div className="flex items-center justify-center w-full h-10 px-1 bg-white hover:bg-gray-200 text-gray-800 rounded-lg border border-gray-300 shadow-md transition duration-150 ease-in-out max-sm:w-12 max-sm:h-12 max-sm:rounded-full max-sm:p-0">
                <button
                  onClick={handleGoogleSignIn}
                  type="button"
                  className="flex items-center justify-center w-full h-full max-sm:p-0"
                >
                  <div className="bg-white rounded-full p-1 sm:mr-2 max-sm:p-0">
                    {values.isLoading ? (
                      <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
                    ) : (
                      <img src="assets/icons/google.png" alt="Google" className="w-5 h-5" />
                    )}
                  </div>
                  <span className="text-sm font-medium sm:block hidden">
                    {values.isLoading ? 'Loading...' : 'Sign in with Google'}
                  </span>
                </button>
              </div>
              <AppleLogin
                clientId={appConfig.appleClientId}
                redirectURI={window.location.origin}
                responseType="code id_token"
                responseMode="fragment"
                usePopup
                callback={responseApple}
                scope="name email"
                render={(renderProps) => (
                  <button
                    onClick={renderProps.onClick}
                    type="button"
                    className="flex items-center text-sm justify-center w-full px-1 bg-black hover:bg-gray-800 text-white rounded-lg shadow-md transition duration-150 ease-in-out max-sm:w-12 max-sm:h-12 max-sm:rounded-full max-sm:p-0"
                  >
                    <img
                      src="assets/icons/apple-icon.svg"
                      alt="Apple logo"
                      className="w-5 h-5 sm:mr-2 max-sm:mr-0"
                    />
                    <span className="sm:block hidden text-center">Sign in with Apple</span>
                  </button>
                )}
              />
            </div>
            <div className="flex justify-between w-4/5 self-center">
              <FormControlLabel
                checked={values.remember}
                onChange={handleChange('remember')}
                control={<Checkbox name="remember" id="remember" color="secondary" />}
                label="Remember me"
              />
              <Button type="text" className="font-bold -ml-1" onClick={_onForgotPasswordClick}>
                Set/Forgot Password
              </Button>
            </div>
            <Button
              isLoading={isSubmitting}
              onClick={handleSubmit}
              className="auth-form-submit-button"
              size="medium"
            >
              Login
            </Button>
            <div className="flex self-center">
              <Typography className="self-center">{"Don't have an account? "}</Typography>
              <Button type="text" className="font-bold -ml-1" onClick={_onSignUpClick}>
                Sign Up
              </Button>
            </div>
          </form>
        );
      }}
    </Formik>
  );
};
